import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Chart, ChartConfiguration } from "chart.js";
import { SvgIconComponent } from "../../../../../../shared/components/svg-icon/svg-icon.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { NgClass } from "@angular/common";
import { createTimeoutSignal } from "../../../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../../../shared/components/timeout/timeout.component";
import { BotevoDesignTokens } from "../../../../../../../assets/themes/botevoPreset";

@Component({
  standalone: true,
  selector: "app-gate-position-chart",
  imports: [TranslateModule, SvgIconComponent, ProgressSpinnerModule, NgClass, TimeoutComponent],
  templateUrl: "./gate-position-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate-position-chart.component.scss",
})
export class GatePositionChartComponent {
  public chartId = input<string>();
  public openCountState = input<number | null>();
  public partiallyOpenCountState = input<number | null>();
  public closedCountState = input<number | null>();

  private translate = inject(TranslateService);
  public loading = computed(() => {
    return (
      this.openCountState() === null || this.partiallyOpenCountState() === null || this.closedCountState() === null
    );
  });

  public timeOut = createTimeoutSignal(this.loading);
  private chart!: Chart;
  public gatePositions: WritableSignal<any[]> = signal([
    {
      label: "gate_position_chart.closed",
      value: null,
      icon: "gate-0",
      color: "primary",
    },
    {
      label: "gate_position_chart.partially_open",
      value: null,
      icon: "gate-50",
      color: "warning",
    },
    {
      label: "gate_position_chart.open",
      value: null,
      icon: "gate-100",
      color: "energy-loss",
    },
  ]);
  @ViewChild("chartCanvas") canvasRef!: ElementRef<HTMLCanvasElement>;
  constructor() {
    effect(() => {
      const openCount = this.openCountState();
      const partiallyOpenCount = this.partiallyOpenCountState();
      const closedCount = this.closedCountState();

      this.gatePositions.update((gatePositions) => {
        gatePositions[0].value = closedCount;
        gatePositions[1].value = partiallyOpenCount;
        gatePositions[2].value = openCount;
        return gatePositions;
      });
      if (this.chart) {
        this.chart.data.datasets[0].data = this.gatePositions().map((position) => position.value);

        this.chart.update();
      } else {
        this.initChart();
      }
    });
  }

  /**
   * Initializes the bar chart.
   * Called when the component is initialized, or when the gate positions change.
   */
  private initChart() {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return; // <- prevents first premature run
    const data = {
      labels: this.gatePositions().map((position) => this.translate.instant(position.label)),
      datasets: [
        {
          label: this.translate.instant("gate_position_chart.title"),
          data: this.gatePositions().map((position) => position.value),

          borderColor: [
            BotevoDesignTokens.primitive?.green?.[400],
            BotevoDesignTokens.primitive?.yellow?.[400],
            BotevoDesignTokens.primitive?.blue?.[200],
          ],

          backgroundColor: [
            `${BotevoDesignTokens.primitive?.green?.[400]}28`,
            `${BotevoDesignTokens.primitive?.yellow?.[400]}28`,
            `${BotevoDesignTokens.primitive?.blue?.[400]}28`,
          ],
        },
      ],
    };
    const config: ChartConfiguration = {
      type: "bar",
      data: data,

      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        // Elements options apply to all of the options unless overridden in a dataset
        // In this case, we are setting the border of each horizontal bar to be 2px wide
        elements: {
          bar: {
            borderWidth: 2,
          },
        },
        scales: {
          y: {
            ticks: {
              display: false,
            },
          },
          x: {
            ticks: {
              stepSize: 1,
            },
          },
        },

        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
        },
      },
    };
    const chartId = this.chartId();
    if (!chartId) {
      return;
    }
    this.chart = new Chart(chartId.toString(), config);
  }
}
